import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDatabase } from '../context/DatabaseContext';
import { useToast } from '../context/ToastContext';
import { getSubAgents } from '../services/agentApi';
import {
    Database,
    Plus,
    Loader,
    Brain
} from 'lucide-react';

/**
 * AIAgentsList - Shows analyzed databases with their sub-agent count.
 * Design based on mark.md image reference.
 */
export const AIAgentsList = () => {
    const { t } = useTranslation(['pages-misc-1', 'common']);
    const navigate = useNavigate();
    const { databases } = useDatabase();
    const { showToast } = useToast();

    const [analyzedDatabases, setAnalyzedDatabases] = useState([]);
    const [loading, setLoading] = useState(false);

    // Fetch analyzed databases with sub-agent counts
    const fetchAnalyzedDatabases = useCallback(async () => {
        setLoading(true);
        try {
            // Filter databases that have been analyzed
            const analyzed = databases.filter(db =>
                db.status && db.status.toLowerCase() === 'analyzed'
            );

            // Fetch sub-agent count for each database
            const databasesWithCounts = await Promise.all(
                analyzed.map(async (db) => {
                    try {
                        const response = await getSubAgents(db.id);
                        const subAgents = response.data?.data?.sub_agents || [];
                        return {
                            ...db,
                            subAgentsCount: subAgents.length
                        };
                    } catch (error) {
                        console.error(`Failed to fetch sub-agents for ${db.name}:`, error);
                        return { ...db, subAgentsCount: 0 };
                    }
                })
            );

            setAnalyzedDatabases(databasesWithCounts);
        } catch (error) {
            console.error('Failed to fetch analyzed databases:', error);
            showToast(t('pages-misc-1:aiAgentsList.failedToLoadDatabases'), 'error');
        } finally {
            setLoading(false);
        }
    }, [databases, showToast, t]);

    useEffect(() => {
        fetchAnalyzedDatabases();
    }, [fetchAnalyzedDatabases]);

    const handleViewSubAgents = (database) => {
        navigate(`/agents/${database.id}`, { state: { database } });
    };

    return (
        <div className="page-content">
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="page-title">{t('pages-misc-1:aiAgentsList.title')}</h1>
                        <p className="page-subtitle">
                            {t('pages-misc-1:aiAgentsList.subtitle')}
                        </p>
                    </div>
                    <button
                        onClick={() => navigate('/database-connection')}
                        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center space-x-2 transition-colors"
                    >
                        <Plus className="w-4 h-4" />
                        <span>{t('pages-misc-1:aiAgentsList.connectNewDatabase')}</span>
                    </button>
                </div>
            </div>

            {/* Databases List */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                        <Database className="w-6 h-6 text-primary-600" />
                        <h2 className="text-lg font-semibold text-gray-900">{t('pages-misc-1:aiAgentsList.databases')}</h2>
                        <span className="px-2.5 py-0.5 bg-gray-100 text-gray-700 rounded-full text-sm">
                            {analyzedDatabases.length}
                        </span>
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center py-12">
                        <Loader className="w-8 h-8 animate-spin text-primary-600" />
                    </div>
                ) : analyzedDatabases.length === 0 ? (
                    <div className="text-center py-12">
                        <Database className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                            {t('pages-misc-1:aiAgentsList.noAnalyzedDatabases')}
                        </h3>
                        <p className="text-gray-600 mb-6">
                            {t('pages-misc-1:aiAgentsList.analyzeToGetStarted')}
                        </p>
                        <button
                            onClick={() => navigate('/schema-analysis')}
                            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 inline-flex items-center space-x-2 transition-colors"
                        >
                            <Brain className="w-5 h-5" />
                            <span>{t('pages-misc-1:aiAgentsList.analyzeDatabase')}</span>
                        </button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {analyzedDatabases.map((db) => (
                            <div
                                key={db.id}
                                onClick={() => handleViewSubAgents(db)}
                                className="border border-gray-200 rounded-xl p-5 hover:border-primary-300 hover:shadow-md transition-all cursor-pointer"
                            >
                                <div className="flex items-center justify-between">
                                    {/* Left Section: Icon + Name */}
                                    <div className="flex items-center space-x-4 flex-1">
                                        <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <Database className="w-6 h-6 text-primary-600" />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-gray-900">{db.name}</h3>
                                            <p className="text-sm text-gray-500">{db.db_type}</p>
                                        </div>
                                    </div>

                                    {/* Sub Agents Count */}
                                    <div className="flex-1">
                                        <p className="text-xs text-gray-500 mb-1">{t('pages-misc-1:aiAgentsList.subAgents')}</p>
                                        <p className="text-xl font-bold text-gray-900">
                                            {db.subAgentsCount}
                                        </p>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex items-center space-x-3 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                        <button
                                            onClick={() => handleViewSubAgents(db)}
                                            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium text-sm transition-colors flex items-center space-x-2"
                                        >
                                            <Brain className="w-4 h-4" />
                                            <span>{t('pages-misc-1:aiAgentsList.viewSubAgents')}</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Add More Section */}
            <div className="border-2 border-dashed border-gray-200 rounded-xl p-12 text-center mt-6">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Plus className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {t('pages-misc-1:aiAgentsList.readyToAddMore')}
                </h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                    {t('pages-misc-1:aiAgentsList.expandCapabilities')}
                </p>
                <button
                    onClick={() => navigate('/database-connection')}
                    className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 inline-flex items-center space-x-2 transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    <span>{t('pages-misc-1:aiAgentsList.connectNewDatabase')}</span>
                </button>
            </div>
        </div>
    );
};
